import React, { useState, useEffect, useCallback } from "react";
import { useField } from "formik";
import { fileService } from "@/services/breeze/FileService";
import { File as AppFile } from "@/types/breeze/File";
import FileDisplay from "@/components/files/FileDisplay";
import { Box, Text, Flex, Button } from "@radix-ui/themes";
import { Field } from "@/types/breeze/Field";
import FileUploadZone from "@/components/files/FileUploadZone";
import { Link, Plus } from "lucide-react";
import {
  validateFileTypes,
  validateFileSizes,
  mbToBytes,
} from "@/utils/fileUtils";
import SideDrawer from "../ui/SideDrawer";
import PaginatedFileGrid from "../files/PaginatedFileGrid";

interface InputFileFieldProps {
  entityId?: number;
  fieldDefinition?: Field;
  name: string;
  label: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
  description?: string;
  multiple?: boolean;
  options: {
    accept: string;
    max_size: number;
    max_files: number;
  };
  restrictedType?: "image" | "document" | undefined;
  onChange?: (fileIds: number[] | number | undefined) => void;
  onEditSaved?: (name: string, value: number[] | number | undefined) => void;
}

type FieldValue =
  | number
  | number[]
  | AppFile
  | AppFile[]
  | { __data: AppFile | AppFile[]; __count: number }
  | undefined;

export default function InputFileField({
  entityId,
  fieldDefinition,
  name,
  label,
  required = false,
  className = "",
  disabled = false,
  description,
  multiple = false,
  options,
  restrictedType = undefined,
  onChange,
  onEditSaved,
}: Readonly<InputFileFieldProps>) {
  const [field, meta, helpers] = useField<FieldValue>(name);
  const [files, setFiles] = useState<AppFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<AppFile[]>([]);

  // Normalize field value to array of IDs or single ID
  const normalizeFieldValue = useCallback(
    (files: AppFile[]) => {
      if (files.length === 0) {
        return multiple ? [] : undefined;
      }

      const ids = files.map((file) => file.id);
      return multiple ? ids : ids[0];
    },
    [multiple],
  );

  // Extract files from various field value formats
  const extractFilesFromValue = useCallback(
    async (value: FieldValue): Promise<AppFile[]> => {
      if (!value) return [];

      // Handle __data format
      if (typeof value === "object" && value !== null && "__data" in value) {
        const dataValue = value.__data;
        return Array.isArray(dataValue) ? dataValue : [dataValue];
      }

      // Handle array of files or IDs
      if (Array.isArray(value)) {
        if (value.length === 0) return [];

        // If array contains file objects
        if (typeof value[0] === "object" && "id" in value[0]) {
          return value as AppFile[];
        }

        // If array contains file IDs
        const filePromises = (value as number[]).map((id) =>
          fileService.getById(id),
        );
        return Promise.all(filePromises);
      }

      // Handle single file object
      if (typeof value === "object" && value !== null && "id" in value) {
        return [value as AppFile];
      }

      // Handle single file ID
      if (typeof value === "number") {
        const file = await fileService.getById(value);
        return [file];
      }

      return [];
    },
    [],
  );

  // Load files when field value changes
  useEffect(() => {
    let mounted = true;

    const loadFiles = async () => {
      setIsLoading(true);
      setValidationError(null);

      try {
        const extractedFiles = await extractFilesFromValue(field.value);

        if (mounted) {
          setFiles(extractedFiles);

          // Normalize the field value if it's not already normalized
          const currentValue = field.value;
          const shouldNormalize =
            currentValue &&
            ((typeof currentValue === "object" && "__data" in currentValue) ||
              (Array.isArray(currentValue) &&
                currentValue.length > 0 &&
                typeof currentValue[0] === "object" &&
                "id" in currentValue[0]) ||
              (typeof currentValue === "object" &&
                currentValue !== null &&
                !Array.isArray(currentValue) &&
                "id" in currentValue));

          if (shouldNormalize) {
            const normalizedValue = normalizeFieldValue(extractedFiles);
            helpers.setValue(normalizedValue);
          }
        }
      } catch (error) {
        console.error("Error loading files:", error);
        if (mounted) {
          setValidationError("Error loading files. Please try again.");
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadFiles();

    return () => {
      mounted = false;
    };
  }, [field.value, extractFilesFromValue, normalizeFieldValue, helpers, name]);

  const validateFiles = (newFiles: File[]): string | null => {
    // Check if required and no files
    if (required && files.length === 0 && newFiles.length === 0) {
      return "This field is required";
    }

    // For single file mode, ensure only one file is selected
    if (!multiple && files.length + newFiles.length > 1) {
      return "Only one file can be selected";
    }

    // Check total number of files (only for multiple mode)
    if (multiple) {
      const totalFiles = files.length + newFiles.length;
      if (totalFiles > options.max_files) {
        return `Maximum ${options.max_files} files allowed`;
      }
    }

    // Check file sizes
    const maxSizeInBytes = mbToBytes(options.max_size);
    const oversizedFiles = validateFileSizes(newFiles, maxSizeInBytes);
    if (oversizedFiles.length > 0) {
      return `Some files exceed the maximum limit of ${options.max_size}MB`;
    }

    // Check file types
    const invalidFiles = validateFileTypes(newFiles, options.accept);
    if (invalidFiles.length > 0) {
      return `Invalid file type. Allowed types: ${options.accept}`;
    }

    return null;
  };

  const handleFileChange = async (newFiles: File[]) => {
    setValidationError(null);

    const error = validateFiles(newFiles);

    if (error) {
      setValidationError(error);
      return;
    }

    setValidationError(null);
    setUploading(true);

    try {
      const uploadPromises = newFiles.map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return await fileService.create(
          formData,
          fieldDefinition?.options?.file?.public ?? false,
          entityId,
          name,
        );
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      // In single file mode, replace existing files
      const updatedFiles = multiple
        ? [...files, ...uploadedFiles]
        : uploadedFiles;

      setFiles(updatedFiles);
      // Update the form field with normalized value
      const fieldValue = normalizeFieldValue(updatedFiles);
      helpers.setValue(fieldValue);
      helpers.setTouched(true);

      // Call onChange callback if provided
      if (onChange) {
        onChange(fieldValue);
      }

      // Call onEditSaved callback if provided
      if (onEditSaved) {
        onEditSaved(name, fieldValue);
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      setValidationError("Error uploading files. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleClearError = () => {
    setValidationError(null);
  };

  const removeFile = (indexToRemove: number) => {
    const newFiles = files.filter((_, index) => index !== indexToRemove);
    setFiles(newFiles);

    // Update the form field with normalized value
    const fieldValue = normalizeFieldValue(newFiles);
    helpers.setValue(fieldValue);
    helpers.setTouched(true);
    setValidationError(null);

    // Call onChange callback if provided
    if (onChange) {
      onChange(fieldValue);
    }

    // Call onEditSaved callback if provided
    if (onEditSaved) {
      onEditSaved(name, fieldValue);
    }
  };

  return (
    <Box className={className}>
      <Box mb={"2"}>
        {label && (
          <Flex align="center" justify="between" mb="2">
            <Text as="label" htmlFor={name} size="2" weight="medium">
              {label} {required && <Text color="red">{" *"}</Text>}
            </Text>
            {!disabled && (
              <Flex gap="2" wrap="wrap">
                <Button
                  title="Add Existing"
                  type="button"
                  size="1"
                  variant="soft"
                  onClick={async () => {
                    setIsDrawerOpen(true);
                  }}
                  style={{
                    minWidth: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flexShrink: 0,
                  }}
                >
                  <Link size={16} />
                </Button>
                <Button
                  title="Upload New"
                  type="button"
                  size="1"
                  variant="soft"
                  onClick={() => {
                    const fileInput = document.createElement("input");
                    fileInput.type = "file";
                    fileInput.multiple = multiple;
                    fileInput.accept = options.accept;
                    fileInput.onchange = (e) => {
                      const files = Array.from(
                        (e.target as HTMLInputElement).files || [],
                      );
                      handleFileChange(files);
                    };
                    fileInput.click();
                  }}
                  style={{
                    minWidth: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flexShrink: 0,
                  }}
                >
                  <Plus size={16} />
                </Button>
              </Flex>
            )}
          </Flex>
        )}

        {description && (
          <Text size="2" color="gray" mb="2" style={{ display: "block" }}>
            {description}
          </Text>
        )}
      </Box>

      {files.length > 0 ? (
        <FileDisplay
          files={files}
          onRemove={!disabled ? removeFile : undefined}
          disabled={disabled}
        />
      ) : (
        <FileUploadZone
          onFileChange={!disabled ? handleFileChange : undefined}
          onClearError={handleClearError}
          accept={options.accept}
          maxSize={options.max_size}
          multiple={multiple}
          loading={uploading || isLoading}
          height="8rem"
        >
          <Box style={{ width: "100%", height: "100%", padding: "1rem" }}>
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "1rem",
                textAlign: "center",
                height: "100%",
              }}
            >
              <Text size="2" color="gray" mb="2">
                {uploading ? (
                  <Text weight="medium">Uploading...</Text>
                ) : isLoading ? (
                  <Text weight="medium">Loading...</Text>
                ) : (
                  <>
                    <Text weight="medium">
                      {multiple
                        ? "Click to upload files"
                        : "Click to upload a file"}
                    </Text>
                  </>
                )}
              </Text>
              <Text size="1" color="gray">
                {options.accept.toUpperCase()} (Max: {options.max_size}MB)
              </Text>
              {multiple && options.max_files > 1 && (
                <Text size="1" color="gray" mt="1">
                  Up to {options.max_files} files
                </Text>
              )}
            </Box>
          </Box>
        </FileUploadZone>
      )}

      {/* Error Messages */}
      {meta.touched && meta.error && (
        <Text size="2" color="red" mt="1">
          {meta.error}
        </Text>
      )}

      {validationError && (
        <Text size="2" color="red" mt="1">
          {validationError}
        </Text>
      )}

      <SideDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedFiles([]);
        }}
        title="Select Existing File(s)"
        saveButton={{
          label: "Save",
          onClick: () => {
            if (selectedFiles.length > 0 && onChange) {
              onChange(
                selectedFiles.length === 1
                  ? selectedFiles[0].id
                  : selectedFiles.map((f) => f.id),
              );
            }
            setIsDrawerOpen(false);
            setSelectedFiles([]);
          },
        }}
      >
        <PaginatedFileGrid
          compact
          enableSelection
          selectedFiles={selectedFiles}
          onSelectionChanged={setSelectedFiles}
          maxFiles={options.max_files}
          showActions={false}
          restrictedType={restrictedType}
        />
      </SideDrawer>
    </Box>
  );
}
